import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Columns2, Rows2 } from "Shared/appIcons";
import {
	ModuleLayoutOrientation,
	useModuleLayout,
} from "Shared/Hooks/useModuleLayout";
import { SimpleTooltip } from "Shared/SimpleTooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Segmented icon pair in the TopBar that switches the calculator module layout
 * between `Row` (inputs stacked above results — the default) and `Column`
 * (inputs left, results right). Mirrors the prototype's "Skelet modulů" switch.
 * The choice is a global, persisted preference read by every <ModuleShell />.
 */
export const ModuleLayoutToggle = () => {
	const { t } = useResource();
	const { orientation, setOrientation, canUseColumn } = useModuleLayout();

	// The column layout is only offered on wide enough screens; on smaller ones
	// the layout is always row, so the toggle would be a no-op — hide it.
	if (!canUseColumn) {
		return null;
	}

	const options = [
		{
			value: ModuleLayoutOrientation.Column,
			Icon: Columns2,
			hint: t(Resources.Modeling.Common.LayoutToggleColumnHint),
		},
		{
			value: ModuleLayoutOrientation.Row,
			Icon: Rows2,
			hint: t(Resources.Modeling.Common.LayoutToggleRowHint),
		},
	] as const;

	return (
		<div className="bg-muted inline-flex items-center rounded-lg p-0.5">
			{options.map(({ value, Icon, hint }) => (
				<SimpleTooltip key={value} content={hint}>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						onClick={() => setOrientation(value)}
						aria-pressed={orientation === value}
						aria-label={hint}
						className={cn(
							"h-7 w-7 rounded-sm",
							orientation === value
								? "bg-background text-primary shadow-sm hover:bg-background hover:text-primary"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						<Icon className="h-4 w-4" />
					</Button>
				</SimpleTooltip>
			))}
		</div>
	);
};
